import { useCallback, useEffect, useState } from 'react';
import useRequestState from '../../hooks/useRequestState';
import { CampaignLetterReview, DisplayCampaignInformation } from '../../types/campaigns.type';
import customIcons from '../../utils/icons';
import Button from '../buttons/baseButton';
import DownloadButton from '../buttons/downloadButton';
import InformationText from '../InformationText';
import { getCampaignCsvUrl } from '../../utils/helpers';
import LetterPreview from '../campaign-confirmation/letterPreview';
import PostApproveCampaignInformation from '../../services/adapters/approveCampaignInformation';
import PostApproveCampaign from '../../services/adapters/approveCampaign';

export default function ApproveSendCampaign({ rowData, onClose }: { rowData: DisplayCampaignInformation, onClose: () => void }) {
  const [showSendButton, setShowSendButton] = useState(false);

  const {
    data: reviewCampaignData,
    error: reviewCampaignError,
    handleRequest: handleReviewRequest,
    FallbackWrapper: ReviewFallbackWrapper,
  } = useRequestState<CampaignLetterReview>();

  const {
    error: sendCampaignError,
    handleRequest: handleSendRequest,
    FallbackWrapper: SendFallbackWrapper,
  } = useRequestState<null>();

  const reviewCampaign = useCallback(async () => {
    const adapter = new PostApproveCampaignInformation();
    if (!rowData.campaignCommunicationId) return;
    const response = adapter.adaptResponse(rowData.campaignCommunicationId);
    await handleReviewRequest(response);
  }, [handleReviewRequest, rowData]);

  useEffect(() => {
    if (!rowData.reviewed && !reviewCampaignData) {
      reviewCampaign();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, reviewCampaignData]);

  const sendCampaign = async () => {
    const adapter = new PostApproveCampaign();
    if (!reviewCampaignData?.mailingGuid) return;
    const response = adapter.adaptResponse(reviewCampaignData?.mailingGuid);
    await handleSendRequest(response);
    if (!sendCampaignError) {
      onClose();
    }
  };

  const errorContent = (
    <div className="flex flex-col items-center justify-center gap-4 p-4 h-full">
      <span className="text-2xl text-pipstatus-alert">{customIcons.errorIcon}</span>
      <span className="text-md text-piptext-body whitespace-pre-line">
        {sendCampaignError?.message || reviewCampaignError?.message }
      </span>
    </div>
  );

  const sendContent = (
    <div className="flex flex-col gap-4">
      <div id="modal-header" className="flex items-center text-start gap-2 w-full">
        <i className="mx-4 text-2xl text-primary">{customIcons.mailWarningIcon}</i>
      </div>
      <span>Are you sure you want to send the campaign?</span>
      <div className="flex justify-evenly items-center w-full gap-4">
        <SendFallbackWrapper loadingMessage="Sending Campaign...">
          <Button
            id="cancel-modal-button"
            label="Cancel"
            className="w-full button-default"
            onClick={onClose}
          />
          <Button
            id="send-campaign-modal-button"
            label="Send"
            className="w-full button-green"
            onClick={sendCampaign}
          />
        </SendFallbackWrapper>
      </div>
    </div>
  );
  const reviewContent = (
    <div className="flex flex-col gap-2 h-full w-full">
      <div id="modal-header" className="flex items-center text-start gap-2 w-full">
        <i className="mx-4 text-2xl text-primary">{customIcons.mailWarningIcon}</i>
        <InformationText title="Approve Campaign" subtitle="" />
      </div>
      <div className="flex flex-col items-center justify-center gap-4 p-4">
        <span>{`There are ${rowData.numberOfRecipients} households eligible for this campaign. Click below to review the entire list.`}</span>
        <DownloadButton
          id={`campaign-table${rowData.campaignCommunicationId}`}
          className="text-xs! button-default pointer-events-auto!"
          fetchUrl={() => getCampaignCsvUrl(rowData)}
        />
        {reviewCampaignData?.letterProofUrl && (<LetterPreview url={reviewCampaignData?.letterProofUrl} />)}
      </div>
      <div className="flex justify-evenly items-center gap-4 m-2 p-2">
        <ReviewFallbackWrapper loadingMessage="updating...">
          <Button
            id="cancel-modal-button"
            label="Cancel"
            className="button-default w-full"
            onClick={onClose}
          />
          <Button
            id="confirm-campaign-modal-button"
            label="Confirm"
            className="button-green w-full"
            onClick={() => setShowSendButton(true)}
          />
        </ReviewFallbackWrapper>
      </div>
    </div>
  );
  const renderContent = () => {
    if (sendCampaignError || reviewCampaignError) {
      return errorContent;
    } if (showSendButton) {
      return sendContent;
    }
    return reviewContent;
  };

  return renderContent();
}
